import { Camera, Check, ExternalLink } from 'lucide-react';
import { FaFacebookF } from 'react-icons/fa';

interface SocialShareConsentProps {
  formId: string;
  value: boolean | null;
  error?: string;
  onChange: (value: boolean) => void;
}

const FACEBOOK_PAGE_URL = 'https://www.facebook.com/edunasserr/';

const CONSENT_OPTIONS: Array<{ value: boolean; label: string }> = [
  { value: true, label: 'أيوه، موافق أشارك فرحتي' },
  { value: false, label: 'لا، شكرًا' },
];

export default function SocialShareConsent({
  formId,
  value,
  error,
  onChange,
}: SocialShareConsentProps) {
  const firstInputId = `${formId}-social_share_consent`;
  const errorId = `${formId}-social_share_consent-error`;

  return (
    // scroll-mt keeps the card clear of the floating site header when it is scrolled into view.
    <section
      id={`${formId}-social_share_section`}
      className="scroll-mt-24 rounded-3xl border border-[#E6E1F2] bg-white p-5 shadow-xs sm:p-6"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#5B3FD0]/10 text-[#5B3FD0]">
          <Camera className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="text-lg font-extrabold text-[#5B3FD0]">شاركنا فرحتك</h2>
      </div>

      <p className="mt-3 text-sm leading-7 text-[#2C2C2C]">
        لما يوصلك خطاب القبول والبوكس بتاعه، صوّرهم وانشر الصورة على السوشيال ميديا واعمل منشن لصفحة EduNasser — وهنشارك صورتك على صفحتنا عشان نحتفل كلنا سوا بنجاحك.
      </p>

      <a
        href={FACEBOOK_PAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group mt-4 flex items-center gap-3 rounded-2xl border border-[#1877F2]/20 bg-[#1877F2]/[0.06] p-3 transition-colors hover:border-[#1877F2]/40 hover:bg-[#1877F2]/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#1877F2]/20"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1877F2] text-white shadow-sm">
          <FaFacebookF className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-extrabold text-[#2C2C2C]">صفحتنا على فيسبوك</span>
          <span dir="ltr" className="block truncate text-end text-xs font-medium text-[#707070]">
            facebook.com/edunasserr
          </span>
        </span>
        <ExternalLink
          className="h-4 w-4 shrink-0 text-[#1877F2] transition-transform group-hover:-translate-y-0.5"
          aria-hidden="true"
        />
      </a>

      <fieldset className="mt-5 border-t border-gray-100 pt-4">
        <legend className="text-sm font-bold text-[#2C2C2C]">
          حابب تشارك فرحتك معانا؟
          <span className="ms-1 text-[#F46A6A]">*</span>
        </legend>

        <div
          role="radiogroup"
          aria-describedby={error ? errorId : undefined}
          className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {CONSENT_OPTIONS.map((opt, idx) => {
            const selected = value === opt.value;
            return (
              <label
                key={String(opt.value)}
                className={`flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 text-sm font-bold transition-colors focus-within:ring-4 focus-within:ring-[#5B3FD0]/15 ${
                  selected
                    ? 'border-[#5B3FD0] bg-[#5B3FD0]/5 text-[#5B3FD0]'
                    : error
                      ? 'border-[#F46A6A]/70 bg-white text-[#2C2C2C] hover:border-[#5B3FD0]/40'
                      : 'border-gray-200 bg-white text-[#2C2C2C] hover:border-[#5B3FD0]/40'
                }`}
              >
                <input
                  id={idx === 0 ? firstInputId : undefined}
                  type="radio"
                  name="social_share_consent"
                  value={String(opt.value)}
                  checked={selected}
                  onChange={() => onChange(opt.value)}
                  className="sr-only"
                />
                <span>{opt.label}</span>
                <span
                  aria-hidden="true"
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    selected
                      ? 'border-[#5B3FD0] bg-[#5B3FD0] text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {selected && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                </span>
              </label>
            );
          })}
        </div>

        {error && (
          <p
            id={errorId}
            role="alert"
            className="mt-2 text-sm font-semibold text-[#F46A6A]"
          >
            {error}
          </p>
        )}
      </fieldset>
    </section>
  );
}
